import axios from 'axios';
import { startAuthentication } from '@simplewebauthn/browser';

// ═══════════════════════════════════════════════════════════════════
//  Action Guard bus — axios response interceptor + cross-component
//  prompt event channel + WebAuthn assertion handler.
//
//  Flow when a guarded route returns 401 ACTION_GUARD_REQUIRED:
//
//    method=pin         → publishes a prompt event so ActionGuardPrompt
//                         shows the PIN modal. On submit it returns
//                         { headers: { X-Action-Pin: ... } } and the
//                         original request retries.
//
//    method=fingerprint → no modal needed for the credential prompt
//                         (the OS handles Touch ID / Windows Hello).
//                         We just kick off the navigator.credentials.get
//                         flow inline, mint the assertion JWT, and retry
//                         with X-WebAuthn-Token. A small status modal
//                         is still shown so the user knows to touch the
//                         sensor / cancel.
// ═══════════════════════════════════════════════════════════════════

let subscriber = null;
let pending = null;
let apiBase = '';

export function configureActionGuardBus(opts) {
  if (opts?.API_BASE) apiBase = opts.API_BASE;
}

export function subscribeActionGuard(fn) {
  subscriber = fn;
  return () => { if (subscriber === fn) subscriber = null; };
}

export function promptActionGuard(ctx = {}) {
  if (pending) return pending.promise;
  let resolve, reject;
  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
  pending = { promise, resolve, reject, ctx };
  if (subscriber) subscriber(ctx);
  return promise;
}

export function resolveActionGuard(result) {
  if (!pending) return;
  const p = pending; pending = null;
  p.resolve(result);
}

export function rejectActionGuard(reason) {
  if (!pending) return;
  const p = pending; pending = null;
  p.reject(reason || new Error('Action guard cancelled'));
}

// Drive the WebAuthn ceremony end-to-end. Returns the assertionToken the
// server minted on /webauthn/auth/verify, or throws.
async function runWebAuthnAssertion() {
  const optsRes = await axios.post(`${apiBase}/action-guard/webauthn/auth/options`, {});
  const { options, handle } = optsRes.data;
  let asserted;
  try {
    asserted = await startAuthentication({ optionsJSON: options });
  } catch (e) {
    // User cancelled OS prompt OR authenticator returned an error.
    throw new Error('Fingerprint cancelled / denied');
  }
  const verifyRes = await axios.post(`${apiBase}/action-guard/webauthn/auth/verify`, {
    handle, response: asserted,
  });
  if (!verifyRes.data?.assertionToken) throw new Error('Server did not return an assertion token');
  return verifyRes.data.assertionToken;
}

export function setupActionGuardInterceptor(API_BASE) {
  if (API_BASE) configureActionGuardBus({ API_BASE });
  if (axios.__oralzyActionGuardAttached) return;
  axios.interceptors.response.use(
    (r) => r,
    async (err) => {
      const data = err?.response?.data;
      if (err?.response?.status !== 401 || data?.code !== 'ACTION_GUARD_REQUIRED') {
        return Promise.reject(err);
      }
      const cfg = err.config || {};
      cfg.headers = cfg.headers || {};

      try {
        if (data.method === 'fingerprint') {
          // Show the prompt as a "Touch sensor…" status modal so the user
          // knows what's happening; the actual credential UI is the OS.
          // We DO NOT await the prompt — the OS prompt is the blocker, and
          // the modal closes itself when the assertion resolves or fails.
          promptActionGuard({ actionKey: data.actionKey, method: 'fingerprint' }).catch(() => {});
          const token = await runWebAuthnAssertion();
          // Close the status modal cleanly.
          resolveActionGuard({});
          cfg.headers['X-WebAuthn-Token'] = token;
        } else {
          const { pin } = await promptActionGuard({ actionKey: data.actionKey, method: 'pin' });
          cfg.headers['X-Action-Pin'] = pin;
        }
        return axios(cfg);
      } catch (innerErr) {
        rejectActionGuard(innerErr);
        return Promise.reject(err);
      }
    },
  );
  axios.__oralzyActionGuardAttached = true;
}
